// Org directory: browse & search every colleague in the company (one Nexus instance
// per company). Reads the authenticated, public-fields-only /v0/directory endpoint and
// filters it live by name/username on the client. Selecting a colleague starts a 1:1.
import React from 'react';
import { FormattedMessage, defineMessages, injectIntl } from 'react-intl';

import LetterTile from './letter-tile.jsx';
import { makeImageUrl } from '../lib/blob-helpers.js';

const messages = defineMessages({
  directory_search_placeholder: {
    id: 'directory_search_placeholder',
    defaultMessage: 'Search by name',
    description: 'Placeholder in the org-directory name filter'
  },
  directory_all_depts: {
    id: 'directory_all_depts',
    defaultMessage: 'All departments',
    description: 'Default option in the org-directory department filter'
  }
});

class Directory extends React.Component {
  constructor(props) {
    super(props);
    this.state = { users: null, filter: '', dept: '', loading: true, error: false };
    this.handleFilter = this.handleFilter.bind(this);
    this.handleDept = this.handleDept.bind(this);
  }

  componentDidMount() {
    const tok = (this.props.tinode.getAuthToken() || {}).token;
    if (!tok) { this.setState({ loading: false, error: true }); return; }
    fetch('/v0/directory', { headers: { 'Authorization': 'token ' + tok } })
      .then(r => { if (!r.ok) { throw new Error('http ' + r.status); } return r.json(); })
      .then(d => {
        const users = (d.users || []).sort((a, b) =>
          (a.fn || a.username || '').localeCompare(b.fn || b.username || ''));
        this.setState({ users: users, loading: false });
      })
      .catch(() => this.setState({ loading: false, error: true }));
  }

  handleFilter(e) { this.setState({ filter: e.target.value }); }
  handleDept(e) { this.setState({ dept: e.target.value }); }

  render() {
    const { formatMessage } = this.props.intl;
    if (this.state.loading) {
      return <div className="dir-note"><FormattedMessage id="directory_loading"
        defaultMessage="Loading directory…" description="Directory loading state" /></div>;
    }
    if (this.state.error) {
      return <div className="dir-note"><FormattedMessage id="directory_error"
        defaultMessage="Could not load the directory" description="Directory error state" /></div>;
    }
    const all = this.state.users || [];
    // Departments present, for the filter dropdown.
    const depts = Array.from(new Set(all.map(u => u.dept).filter(Boolean))).sort((a, b) => a.localeCompare(b));
    const f = this.state.filter.trim().toLowerCase();
    const list = all.filter(u => {
      if (this.state.dept && u.dept !== this.state.dept) { return false; }
      if (!f) { return true; }
      return (u.fn || '').toLowerCase().indexOf(f) >= 0 ||
        (u.username || '').toLowerCase().indexOf(f) >= 0 ||
        (u.title || '').toLowerCase().indexOf(f) >= 0 ||
        (u.dept || '').toLowerCase().indexOf(f) >= 0;
    });
    return (
      <div className="flex-column">
        <div className="panel-form">
          <div className="panel-form-row">
            <i className="material-icons search">search</i>
            <input className="search" type="text"
              placeholder={formatMessage(messages.directory_search_placeholder)}
              value={this.state.filter} onChange={this.handleFilter} autoFocus />
          </div>
          {depts.length > 0 ?
            <div className="panel-form-row">
              <i className="material-icons search">apartment</i>
              <select className="dir-dept-select" value={this.state.dept} onChange={this.handleDept}>
                <option value="">{formatMessage(messages.directory_all_depts)}</option>
                {depts.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div> : null}
        </div>
        {list.length === 0 ?
          <div className="dir-note">
            {this.state.filter ?
              <FormattedMessage id="directory_no_match" defaultMessage="No one matches"
                description="Directory: no filter match" /> :
              <FormattedMessage id="directory_empty" defaultMessage="No colleagues yet"
                description="Directory: empty" />}
          </div> :
          <ul className="dir-list">
            {list.map(u =>
              <li key={u.uid} className="dir-item" onClick={() => this.props.onSelect(u.uid)}>
                <div className="dir-avatar">
                  <LetterTile tinode={this.props.tinode}
                    authorizeURL={this.props.tinode.authorizeURL.bind(this.props.tinode)}
                    avatar={makeImageUrl(u.photo) || true}
                    topic={u.uid} title={u.fn || u.username} />
                  {u.online ? <span className="online" title="в сети" /> : null}
                </div>
                <div className="dir-text">
                  <div className="dir-name">{u.fn || u.username || u.uid}</div>
                  {(u.title || u.dept) ?
                    <div className="dir-role">{[u.title, u.dept].filter(Boolean).join(' · ')}</div> :
                    (u.username ? <div className="dir-username">@{u.username}</div> : null)}
                </div>
              </li>
            )}
          </ul>}
      </div>
    );
  }
}

export default injectIntl(Directory);
